import React, { useState } from 'react';
import { FieldLabel } from '@measured/puck';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CustomTooltip from '../../../components/common/tooltip';
import InputField from '../../../components/common/input-field';
import { ICardFields } from '../../interface/formInterface';
import { Select } from '../../../page-builder/components/form-fields/Select';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import { fontFamilies } from '../../puck/reusable-props/options';
import { placeholderfontWeightOptions } from '../../puck/reusable-props/TextCustomInput';
import {
  placeHolderHeightOptions,
  textAlignOption,
} from '../../puck/reusable-props/height';
import BackgroungColorComponent from '../customComponents/BackgroungColorComponent';
import Border from '../../puck/reusable-props/Border';
import Outline from '../../puck/reusable-props/Outline';

interface cardFieldProps {
  value: ICardFields;
  onChange: (_value: ICardFields) => void;
  name: string;
}
export const CardFieldOption = ({ value, onChange, name }: cardFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const separateWords = (input: string) => {
    if (!input) {
      return '';
    }
    return input.replace(/([a-z])([A-Z])/g, '$1 $2');
  };
  return (
    <div>
      <div
        className="flex justify-between font-bold cursor-pointer py-2 bg-slate-100 rounded-md pagebuilder-theme"
        onClick={() => setIsOpen(!isOpen)}>
        <h3>{separateWords(name)}</h3>
        {!isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
      </div>
      {isOpen && (
        <div className="flex flex-col gap-3 border border-gray-300 rounded p-2 w-full">
          {
            <>
              {name != 'Product Name Customization' &&
                name != 'Subtitle Customization' && (
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="Text">
                          <span>Placeholder</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div">
                    <InputField
                      value={value?.placeholder}
                      onChange={text =>
                        onChange({ ...value, placeholder: text })
                      }
                    />
                  </FieldLabel>
                )}

              <ColorPicker
                value={value?.color} // Assuming `value.color` holds the color information
                onChange={newColor => {
                  onChange({ ...value, color: newColor });
                }}
                label="Color"
                tooltip="Choose a color for the checkbox"
              />

              <FieldLabel
                label={
                  (
                    <CustomTooltip title="Text">
                      <span>FontSize</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <InputField
                  value={value?.fontSize}
                  onChange={e => {
                    let newValue = e.replace(/[^0-9%]/g, '');

                    // Ensure that only one '%' symbol remains at the end
                    if (newValue?.includes('%')) {
                      newValue =
                        newValue.replace(/%.*%/g, '%').replace(/%/g, '') + '%';
                    }

                    // If the string is empty or contains only whitespace, set to 'auto'
                    if (!newValue.trim()) {
                      newValue = 'auto';
                    }

                    // If the value includes both digits and '%', clean up any non-digit/% characters
                    if (/\d/.test(newValue) && /%/.test(newValue)) {
                      newValue = newValue.replace(/[^0-9%]/g, '');
                    }

                    // Default to 'auto' if the value is still empty
                    if (!newValue.trim()) {
                      newValue = 'auto';
                    }

                    onChange({ ...value, fontSize: newValue });
                  }}
                />
              </FieldLabel>
              <FieldLabel
                label={
                  (
                    <CustomTooltip title="fontWeight">
                      <span>Font Weight</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <Select
                  value={value?.fontWeight}
                  onChange={event =>
                    onChange({
                      ...value,
                      fontWeight: event.target.value,
                    })
                  }
                  options={placeholderfontWeightOptions}
                />
              </FieldLabel>

              <FieldLabel
                label={
                  (
                    <CustomTooltip title="fontWeight">
                      <span>Line Height</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <Select
                  value={value?.lineHeight}
                  onChange={event =>
                    onChange({
                      ...value,
                      lineHeight: event.target.value,
                    })
                  }
                  options={placeHolderHeightOptions}
                />
              </FieldLabel>
              <FieldLabel
                label={
                  (
                    <CustomTooltip title="Text">
                      <span>Letter Space</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <InputField
                  value={value?.letterSpace}
                  onChange={e => {
                    const text = e.replace(/[^0-9%]/g, '');
                    onChange({ ...value, letterSpace: text });
                  }}
                />
              </FieldLabel>
              {name !== 'Title' && (
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="fontWeight">
                        <span>Text Align</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div">
                  <Select
                    value={value?.textAlign}
                    onChange={event =>
                      onChange({
                        ...value,
                        textAlign: event.target.value,
                      })
                    }
                    options={textAlignOption}
                  />
                </FieldLabel>
              )}

              <FieldLabel
                label={
                  (
                    <CustomTooltip title="fontWeight">
                      <span>Font Family</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <Select
                  value={value?.fontFamily}
                  onChange={event =>
                    onChange({
                      ...value,
                      fontFamily: event.target.value,
                    })
                  }
                  options={fontFamilies}
                />
              </FieldLabel>
              {name !== 'Title' &&
                name !== 'Product Name Customization' &&
                name !== 'Subtitle Customization' && (
                  <>
                    <BackgroungColorComponent
                      value={value.textColor || { color: '', isInherit: false }}
                      onChange={newColor => {
                        onChange({ ...value, textColor: newColor });
                      }}
                      title="Input Text Color"
                      label="Input Text Color"
                    />
                    <BackgroungColorComponent
                      value={
                        value.textBackgroundColor || {
                          color: '',
                          isInherit: false,
                        }
                      }
                      onChange={newColor => {
                        onChange({ ...value, textBackgroundColor: newColor });
                      }}
                      title="Background Color"
                    />
                    <Border
                      value={value.border}
                      onChange={newBorder => {
                        onChange({ ...value, border: newBorder });
                      }}
                      title="Border"
                    />
                    <Outline
                      value={value.outline}
                      onChange={newOutline => {
                        onChange({ ...value, outline: newOutline });
                      }}
                      title="Outline"
                    />
                  </>
                )}
            </>
          }
        </div>
      )}
    </div>
  );
};
